import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DailyTotals, MetricPeriod, FactorAnalysisResult, MetricsSummary } from '@/types'
import { PlanData } from '@/services/analyticsService'
import { calculateFactorAnalysis } from '@/lib/analytics'
import { supabase } from '@/lib/supabase'
import { UseFactorAnalysisReturn } from './useFactorAnalysis'
import { hasAnyPositive, getChannelPresence } from '@/lib/analytics/subscriptionPresence'

export interface UseMultiStudioFactorAnalysisParams {
  studioIds: number[]
  dailyData: DailyTotals[]
  currentPeriod: MetricPeriod | null
  planData: PlanData | null
  periodMetrics?: MetricsSummary | null
}

export interface FactorAnalysisVariants {
  mixed: FactorAnalysisResult | null
  regular: FactorAnalysisResult | null
  subscription: FactorAnalysisResult | null
}

export interface UseMultiStudioFactorAnalysisReturn extends UseFactorAnalysisReturn {
  variants: FactorAnalysisVariants
  canShowSplitViews: boolean
}

type SummableField =
  | 'leads_count'
  | 'appointments_count'
  | 'new_visitors_count'
  | 'new_sales_count'
  | 'new_sales_amount'
  | 'membership_fees_new_amount'
  | 'new_subscriptions_count'
  | 'new_subscriptions_amount'

const SUM_FIELDS: SummableField[] = [
  'leads_count',
  'appointments_count',
  'new_visitors_count',
  'new_sales_count',
  'new_sales_amount',
  'membership_fees_new_amount',
  'new_subscriptions_count',
  'new_subscriptions_amount'
]

function aggregateDailyByDate(rows: DailyTotals[]): DailyTotals[] {
  const map = new Map<string, DailyTotals>()

  rows.forEach((row) => {
    const date = row.metric_date
    const current = map.get(date)

    if (!current) {
      map.set(date, {
        studio_internal_id: 0,
        metric_date: date,
        created_at: row.created_at || '',
        updated_at: row.updated_at || '',
        leads_count: row.leads_count || 0,
        appointments_count: row.appointments_count || 0,
        new_visitors_count: row.new_visitors_count || 0,
        new_sales_count: row.new_sales_count || 0,
        new_sales_amount: row.new_sales_amount || 0,
        membership_fees_new_amount: row.membership_fees_new_amount || 0,
        new_subscriptions_count: row.new_subscriptions_count || 0,
        new_subscriptions_amount: row.new_subscriptions_amount || 0
      })
      return
    }

    SUM_FIELDS.forEach((field) => {
      current[field] = (current[field] || 0) + (row[field] || 0)
    })
  })

  return Array.from(map.values()).sort((a, b) => a.metric_date.localeCompare(b.metric_date))
}

function getLocalToday(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

function projectDailyDataByChannel(rows: DailyTotals[], channel: 'mixed' | 'regular' | 'subscription'): DailyTotals[] {
  if (channel === 'mixed') {
    return rows
  }

  return rows.map((row) => {
    if (channel === 'regular') {
      return {
        ...row,
        new_subscriptions_count: 0,
        new_subscriptions_amount: 0,
        membership_fees_new_amount: 0
      }
    }

    return {
      ...row,
      new_sales_count: 0,
      new_sales_amount: 0
    }
  })
}

function projectPlanDataByChannel(planData: PlanData | null, channel: 'mixed' | 'regular' | 'subscription'): PlanData | null {
  if (!planData || channel === 'mixed') {
    return planData
  }

  if (channel === 'regular') {
    return {
      ...planData,
      new_subscriptions_count_plan: 0,
      new_subscriptions_amount_plan: 0,
      membership_fees_new_plan: 0
    }
  }

  return {
    ...planData,
    new_sales_count_plan: 0,
    new_sales_amount_plan: 0
  }
}

export function useMultiStudioFactorAnalysis({
  studioIds,
  dailyData,
  currentPeriod,
  planData,
  periodMetrics
}: UseMultiStudioFactorAnalysisParams): UseMultiStudioFactorAnalysisReturn {
  const enabled = studioIds.length > 0 && !!currentPeriod

  const { data: rollingDailyData = [], isLoading, error } = useQuery<DailyTotals[], Error>({
    queryKey: ['factor-analysis-rolling-multi', [...studioIds].sort().join(','), getLocalToday()],
    queryFn: async () => {
      const today = getLocalToday()
      const end = new Date(`${today}T00:00:00`)
      const start = new Date(end)
      start.setDate(start.getDate() - 27)
      const startStr = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`

      const { data, error } = await supabase
        .from('studio_daily_totals')
        .select(
          'studio_internal_id,metric_date,leads_count,appointments_count,new_visitors_count,new_sales_count,new_sales_amount,membership_fees_new_amount,new_subscriptions_count,new_subscriptions_amount,created_at,updated_at'
        )
        .in('studio_internal_id', studioIds)
        .gte('metric_date', startStr)
        .lte('metric_date', today)
        .order('metric_date', { ascending: true })

      if (error) {
        throw new Error(`Failed to load multi-studio rolling daily totals: ${error.message}`)
      }

      return aggregateDailyByDate((data || []) as DailyTotals[])
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })

  const variants = useMemo<FactorAnalysisVariants>(() => {
    if (!currentPeriod) {
      return {
        mixed: null,
        regular: null,
        subscription: null
      }
    }
    const mixed = calculateFactorAnalysis({
      rollingDailyData: projectDailyDataByChannel(rollingDailyData, 'mixed'),
      periodDailyData: projectDailyDataByChannel(dailyData, 'mixed'),
      currentPeriod,
      planData: projectPlanDataByChannel(planData, 'mixed')
    })
    const regular = calculateFactorAnalysis({
      rollingDailyData: projectDailyDataByChannel(rollingDailyData, 'regular'),
      periodDailyData: projectDailyDataByChannel(dailyData, 'regular'),
      currentPeriod,
      planData: projectPlanDataByChannel(planData, 'regular')
    })
    const subscription = calculateFactorAnalysis({
      rollingDailyData: projectDailyDataByChannel(rollingDailyData, 'subscription'),
      periodDailyData: projectDailyDataByChannel(dailyData, 'subscription'),
      currentPeriod,
      planData: projectPlanDataByChannel(planData, 'subscription')
    })

    return { mixed, regular, subscription }
  }, [rollingDailyData, dailyData, currentPeriod, planData])

  const data = variants.mixed || null

  const splitViewDebug = useMemo(() => {
    const noContext = !currentPeriod || studioIds.length === 0
    if (noContext) {
      return {
        noContext,
        studioIdsCount: studioIds.length,
        periodId: currentPeriod?.period_id || null,
        regularFactCount: 0,
        regularFactAmount: 0,
        subscriptionFactCount: 0,
        subscriptionFactAmount: 0,
        hasRegularFact: false,
        hasSubscriptionFact: false,
        hasRegularPlan: false,
        hasSubscriptionPlan: false,
        canShowSplitViews: false
      }
    }

    const regularFactCount = dailyData.reduce((sum, day) => sum + (day.new_sales_count || 0), 0)
    const regularFactAmount = dailyData.reduce((sum, day) => sum + (day.new_sales_amount || 0), 0)
    const subscriptionFactCount = dailyData.reduce((sum, day) => sum + (day.new_subscriptions_count || 0), 0)
    const subscriptionFactAmount = dailyData.reduce(
      (sum, day) => sum + (day.new_subscriptions_amount || 0) + (day.membership_fees_new_amount || 0),
      0
    )
    const factPresence = getChannelPresence(
      [regularFactCount, regularFactAmount],
      [subscriptionFactCount, subscriptionFactAmount]
    )

    const hasRegularFactFromMetrics = hasAnyPositive([periodMetrics?.total_sales, periodMetrics?.new_sales_amount])
    const hasSubscriptionFactFromMetrics = hasAnyPositive([
      periodMetrics?.new_subscriptions_count,
      periodMetrics?.new_subscriptions_amount,
      periodMetrics?.membership_fees_new_amount
    ])

    const hasRegularPlan = hasAnyPositive([planData?.new_sales_count_plan, planData?.new_sales_amount_plan])
    const hasSubscriptionPlan = hasAnyPositive([
      planData?.new_subscriptions_count_plan,
      planData?.new_subscriptions_amount_plan,
      planData?.membership_fees_new_plan
    ])

    const hasRegular = factPresence.hasRegular || hasRegularFactFromMetrics || hasRegularPlan
    const hasSubscription = factPresence.hasSubscription || hasSubscriptionFactFromMetrics || hasSubscriptionPlan
    return {
      noContext,
      studioIdsCount: studioIds.length,
      periodId: currentPeriod?.period_id || null,
      regularFactCount,
      regularFactAmount,
      subscriptionFactCount,
      subscriptionFactAmount,
      hasRegularFact: factPresence.hasRegular,
      hasSubscriptionFact: factPresence.hasSubscription,
      hasRegularFactFromMetrics,
      hasSubscriptionFactFromMetrics,
      hasRegularPlan,
      hasSubscriptionPlan,
      canShowSplitViews: hasRegular && hasSubscription
    }
  }, [currentPeriod, studioIds.length, dailyData, planData, periodMetrics])
  const canShowSplitViews = splitViewDebug.canShowSplitViews

  return {
    data,
    variants,
    canShowSplitViews,
    loading: isLoading,
    error: error ? error.message : null,
    hasInsufficientData: !!data && data.factors.length === 0
  }
}
